import { useCallback, useState } from "react";

import { Box } from "@mui/material";
import mapboxgl from "mapbox-gl";
import Map, { Layer, Marker, Source } from "react-map-gl/mapbox";

import { EVOTRUX_BLUE } from "../../../../../../constants";

import type { Feature, LineString } from "geojson";
import type { ViewStateChangeEvent } from "react-map-gl/mapbox";

const TOKEN = import.meta.env.REACT_APP_MAPBOX_PUBLIC_KEY ?? "ERROR";

const DEFAULT_ZOOM = 4;

/**
 * Structural rather than `SuggestedRouteNavigation`: the unauthenticated
 * submit-quote command-link landing publishes a strict allowlist and draws this
 * same map, and the geometry is all this component reads off the row. A full
 * navigation row still satisfies it.
 */
interface NavigationLikeRoute {
  payload: SuggestedRouteNavigationPayload;
}

interface Props {
  navigation: NavigationLikeRoute;
  origin: RoutePointCoordinates;
  destination: RoutePointCoordinates;
  midPoints: RoutePointCoordinates[];
}

interface MapState {
  zoom: number;
  latitude: number;
  longitude: number;
}

export function SuggestedNavigationRouteMap(props: Props) {
  const { navigation, origin, destination, midPoints } = props;

  const [mapState, setMapState] = useState<MapState>({
    latitude: origin.latitude,
    longitude: origin.longitude,
    // the defualt value will need to be a coordinate that shows the whole
    // map, which also affects the zoom label
    zoom: DEFAULT_ZOOM,
  });

  const fitMapToBounds = useCallback(
    (map: mapboxgl.Map) => {
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([origin.longitude, origin.latitude]);
      bounds.extend([destination.longitude, destination.latitude]);

      map.fitBounds(bounds, {
        padding: 100, // Padding around the markers
        duration: 1000, // Animation duration
      });
    },
    [origin, destination],
  );

  const handleMapLoad = useCallback(
    (event: any) => {
      const map = event.target;
      map.setLayoutProperty("poi-label", "visibility", "none");
      fitMapToBounds(map);
    },
    [fitMapToBounds],
  );

  const validGeoJSON: Feature<LineString> = {
    type: "Feature",
    properties: {},
    geometry: navigation.payload.routes[0].geometry,
  };

  return (
    <Box id="map-conatiner" sx={{ width: "100%", height: "50vh" }}>
      <Map
        reuseMaps
        mapboxAccessToken={TOKEN}
        longitude={mapState.longitude}
        latitude={mapState.latitude}
        zoom={mapState.zoom}
        style={{ width: "100%", height: "100%" }}
        onMove={(evt: ViewStateChangeEvent) =>
          setMapState((prev) => ({
            ...prev,
            latitude: evt.viewState.latitude,
            longitude: evt.viewState.longitude,
            zoom: evt.viewState.zoom,
          }))
        }
        mapStyle="mapbox://styles/mapbox/streets-v11"
        onLoad={handleMapLoad}
      >
        <Marker
          key="origin"
          longitude={origin.longitude}
          latitude={origin.latitude}
        />
        <Marker
          key="destination"
          longitude={destination.longitude}
          latitude={destination.latitude}
        />
        {midPoints.map((rp) => (
          <Marker
            key={rp.point_number}
            longitude={rp.longitude}
            latitude={rp.latitude}
          />
        ))}
        {navigation.payload.routes.length > 0 && (
          <Source id="route-source" type="geojson" data={validGeoJSON}>
            <Layer
              id="route-layer"
              type="line"
              paint={{
                "line-color": EVOTRUX_BLUE,
                "line-width": 4,
              }}
            />
          </Source>
        )}
      </Map>
    </Box>
  );
}
